import { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQuery } from '@tanstack/react-query';
import {
  timesheetEntryInputSchema,
  type TimesheetEntryInput,
  type Paginated,
} from '@timesheet/shared';
import { apiOrThrow } from '@/lib/api';
import { Modal } from '@/components/ui/Modal';
import { Field } from '@/components/ui/Field';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { Button } from '@/components/ui/Button';

interface ClientOpt {
  id: string;
  code: string;
  name: string;
}
interface EngagementOpt {
  id: string;
  clientId: string;
  code: string;
  name: string;
  fiscalYear: number;
}
interface ActivityOpt {
  id: string;
  code: string;
  name: string;
}

interface EntryFormModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (input: TimesheetEntryInput) => Promise<void>;
  defaultValues?: Partial<TimesheetEntryInput>;
  title: string;
}

/**
 * Timesheet entry form.
 *
 * UX flow:
 *   1. Pick client → engagement dropdown filters to that client only
 *   2. Pick engagement → activity dropdown enables
 *   3. Pick activity → hours + description appear
 *
 * Master data is loaded once via TanStack Query (cached across modal opens).
 */
export function EntryFormModal({
  open,
  onClose,
  onSubmit,
  defaultValues,
  title,
}: EntryFormModalProps) {
  const {
    register,
    handleSubmit,
    watch,
    setValue,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<TimesheetEntryInput>({
    resolver: zodResolver(timesheetEntryInputSchema),
    defaultValues: {
      workDate: new Date().toISOString().slice(0, 10),
      clientId: '',
      engagementId: '',
      activityId: '',
      hours: 1,
      description: '',
      ...defaultValues,
    },
  });

  // Load master data
  const clientsQuery = useQuery({
    queryKey: ['clients', 'options'],
    queryFn: () =>
      apiOrThrow<Paginated<ClientOpt>>('/admin/clients?active=true&pageSize=500'),
    staleTime: 60_000,
  });
  const engagementsQuery = useQuery({
    queryKey: ['engagements', 'options'],
    queryFn: () =>
      apiOrThrow<Paginated<EngagementOpt>>('/admin/engagements?active=true&pageSize=500'),
    staleTime: 60_000,
  });
  const activitiesQuery = useQuery({
    queryKey: ['activities', 'options'],
    queryFn: () =>
      apiOrThrow<Paginated<ActivityOpt>>('/admin/activities?active=true&pageSize=500'),
    staleTime: 60_000,
  });

  const selectedClientId = watch('clientId');
  const selectedEngagementId = watch('engagementId');

  // Filter engagements by selected client
  const engagementOptions = useMemo(() => {
    const all = engagementsQuery.data?.items ?? [];
    if (!selectedClientId) return [];
    return all.filter((e) => e.clientId === selectedClientId);
  }, [engagementsQuery.data, selectedClientId]);

  // Clear engagement if client changes and current engagement doesn't belong
  useEffect(() => {
    if (!selectedClientId) return;
    if (selectedEngagementId) {
      const stillValid = engagementOptions.some((e) => e.id === selectedEngagementId);
      if (!stillValid) setValue('engagementId', '');
    }
  }, [selectedClientId, selectedEngagementId, engagementOptions, setValue]);

  // Reset form when modal closes
  useEffect(() => {
    if (!open) {
      reset({
        workDate: new Date().toISOString().slice(0, 10),
        clientId: '',
        engagementId: '',
        activityId: '',
        hours: 1,
        description: '',
      });
    }
  }, [open, reset]);

  const submit = handleSubmit(async (values) => {
    try {
      await onSubmit(values);
    } catch (e) {
      alert((e as Error).message);
    }
  });

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={title}
      size="lg"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} type="button">
            Cancel
          </Button>
          <Button onClick={submit} loading={isSubmitting} type="button">
            Save as draft
          </Button>
        </>
      }
    >
      <form onSubmit={submit} className="space-y-4">
        <Field label="Work date" error={errors.workDate?.message} required>
          {(p) => <Input {...p} type="date" {...register('workDate')} />}
        </Field>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Field label="Client" error={errors.clientId?.message} required>
            {(p) => (
              <Select {...p} {...register('clientId')}>
                <option value="">— select —</option>
                {clientsQuery.data?.items.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.code} — {c.name}
                  </option>
                ))}
              </Select>
            )}
          </Field>

          <Field
            label="Engagement"
            error={errors.engagementId?.message}
            required
            hint={!selectedClientId ? 'Pick a client first' : undefined}
          >
            {(p) => (
              <Select
                {...p}
                {...register('engagementId')}
                disabled={!selectedClientId || engagementOptions.length === 0}
              >
                <option value="">— select —</option>
                {engagementOptions.map((e) => (
                  <option key={e.id} value={e.id}>
                    {e.code} — {e.name} (FY{e.fiscalYear})
                  </option>
                ))}
              </Select>
            )}
          </Field>
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Field label="Activity" error={errors.activityId?.message} required>
            {(p) => (
              <Select {...p} {...register('activityId')}>
                <option value="">— select —</option>
                {activitiesQuery.data?.items.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.code} — {a.name}
                  </option>
                ))}
              </Select>
            )}
          </Field>

          <Field
            label="Hours"
            error={errors.hours?.message}
            required
            hint="0.25 increments, max 24"
          >
            {(p) => (
              <Input
                {...p}
                type="number"
                step="0.25"
                min="0.25"
                max="24"
                {...register('hours', { valueAsNumber: true })}
              />
            )}
          </Field>
        </div>

        <Field label="Description" error={errors.description?.message} hint="Optional, max 500 chars">
          {(p) => (
            <textarea
              {...p}
              {...register('description')}
              rows={3}
              maxLength={500}
              placeholder="What did you work on?"
              className="w-full rounded border border-brand-gray-light bg-white px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-blue"
            />
          )}
        </Field>
      </form>
    </Modal>
  );
}
